import { ReactNode, useState } from 'react';
import { User, ChevronRight, ChevronDown, LucideIcon, FileText, Network } from 'lucide-react';
import VerityTopControls from './components/VerityTopControls';
import { ReviewMode } from './components/useReviewMode';
import './verity.css';

/** The dark logo only: this module has one theme and it is dark. */
const VERITY_LOGO = `${process.env.PUBLIC_URL || ''}/IQVerity_logo_Dark.png`;

export interface VerityNavItem {
  key: string;
  label: string;
  icon: LucideIcon;
  /** Render indented, as a child of the nearest un-indented item above it. */
  indent?: boolean;
  /** Pushed to the bottom of the sidebar, below a separating gap. */
  footer?: boolean;
}

interface VerityLayoutProps {
  navItems: VerityNavItem[];
  active: string;
  onNavigate: (key: string) => void;
  activeLabel: string;
  userName?: string;
  /** Engineering or AWP Review. Both shells pass it; see useReviewMode. */
  mode: ReviewMode;
  onModeChange: (mode: ReviewMode) => void;
  children: ReactNode;
}

const MODES: { key: ReviewMode; label: string; icon: LucideIcon }[] = [
  { key: 'engineering', label: 'Engineering', icon: FileText },
  { key: 'awp', label: 'AWP', icon: Network },
];

/** Full IQ Verity shell: fixed sidebar + sticky topbar + content area.
 *  Everything lives under `.vr-root` so the dark theme never leaks out. */
const VerityLayout = ({
  navItems, active, onNavigate, activeLabel,
  userName = '', mode, onModeChange, children,
}: VerityLayoutProps) => {
  // Parent keys the user has collapsed; groups start expanded.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggle = (key: string) =>
    setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  const renderNavLink = (item: VerityNavItem, opts?: { childActive?: boolean }) => {
    const Icon = item.icon;
    const isActive = item.key === active || !!opts?.childActive;
    return (
      <button
        key={item.key}
        type="button"
        onClick={() => onNavigate(item.key)}
        className={`vr-navlink${item.indent ? ' vr-navlink-sub' : ''}${isActive ? ' vr-active' : ''}`}
      >
        <Icon size={16} />
        <span>{item.label}</span>
      </button>
    );
  };

  /** Walk a flat list, attaching each run of `indent` items to the item above it. */
  const renderList = (items: VerityNavItem[]) => {
    const out: ReactNode[] = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.indent) continue; // rendered with its parent

      const kids: VerityNavItem[] = [];
      for (let j = i + 1; j < items.length && items[j].indent; j += 1) kids.push(items[j]);

      if (!kids.length) {
        out.push(renderNavLink(item));
        continue;
      }

      const isOpen = !collapsed[item.key];
      const childActive = kids.some((k) => k.key === active);
      out.push(
        <div className="vr-navrow" key={item.key}>
          {renderNavLink(item, { childActive: !isOpen && childActive })}
          <button
            type="button"
            className="vr-nav-caret"
            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${item.label}`}
            aria-expanded={isOpen}
            onClick={() => toggle(item.key)}
          >
            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>
      );
      if (isOpen) kids.forEach((k) => out.push(renderNavLink(k)));
    }
    return out;
  };

  const mainItems = navItems.filter((i) => !i.footer);
  const footItems = navItems.filter((i) => i.footer);

  return (
    <div className={`vr-root${mode === 'awp' ? ' vr-theme-awp' : ''}`}>
      <div className="vr-app">
        <aside className="vr-sidebar">
          <div className="vr-sidebar-head">
            {/* The logo carries the wordmark AND the strapline, so the shield, the "IQ VERITY" text
                and the client label it replaces would all be saying it twice. */}
            <img className="vr-logo-img" src={VERITY_LOGO} alt="IQ Verity" />
          </div>

          {/* Directly under the logo: it decides what every screen below it shows, so it sits above
              the navigation rather than beside a page title. */}
          <div className="vr-mode-switch" role="radiogroup" aria-label="Review type">
            {MODES.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.key}
                  type="button"
                  role="radio"
                  aria-checked={mode === m.key}
                  className={`vr-mode-btn${mode === m.key ? ' vr-active' : ''}`}
                  onClick={() => mode !== m.key && onModeChange(m.key)}
                >
                  <Icon size={13} />
                  <span>{m.label}</span>
                </button>
              );
            })}
          </div>

          <nav className="vr-nav">
            {renderList(mainItems)}
            {footItems.length > 0 && (
              <div className="vr-nav-foot-group">
                {renderList(footItems)}
              </div>
            )}
          </nav>

          <div className="vr-sidebar-foot">
            <div className="vr-user-row">
              <div className="vr-avatar"><User size={14} /></div>
              <span className="vr-user-name">{userName}</span>
            </div>
          </div>
        </aside>

        <main className="vr-main">
          <div className="vr-topbar">
            <div className="vr-crumbs">
              <span>{mode === 'awp' ? 'AWP Review' : 'Engineering Review'}</span>
              <ChevronRight size={12} />
              <span className="vr-crumb-here">{activeLabel}</span>
            </div>
            <VerityTopControls />
          </div>
          <div className="vr-content vr-gradient-mesh">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default VerityLayout;
